import { useEffect, useState } from "react";
import {
  fetchAccountSubscriptionHistory,
  SubscriptionPeriodRow,
} from "api/subscriptionPeriods";

export interface AccountSubscriptionHistory {
  periods: SubscriptionPeriodRow[];
  isLoading: boolean;
  error: string | null;
}

// Lazily loads an account's subscription periods. Both the lifetime-value
// header and the timeline read from this single fetch.
export function useAccountSubscriptionHistory(
  accountId: string,
): AccountSubscriptionHistory {
  const [periods, setPeriods] = useState<SubscriptionPeriodRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId) {
      setPeriods([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchAccountSubscriptionHistory(accountId)
      .then((data) => {
        if (cancelled) return;
        setPeriods(data.periods ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching subscription history:", err);
        setError("Failed to load subscription history.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId]);

  return { periods, isLoading, error };
}
